import React, { useState } from 'react';
import { Building2, CheckCircle2, Info, Layers, AlertTriangle } from 'lucide-react';
import { fmt$, NAVY_GRADIENT, ACCENT_BAR_GRADIENT } from './constants';
import { BRAND } from '../kit/tokens';

// ============================================================================
// HybridCalculation — multi-source reconciled qualifying-income view
// ============================================================================
export function HybridCalculation({ hybrid }) {
  const [expanded, setExpanded] = useState(new Set(hybrid.employers.map((_, i) => i)));
  const toggle = (i) => {
    const s = new Set(expanded);
    s.has(i) ? s.delete(i) : s.add(i);
    setExpanded(s);
  };

  return (
    <div className="rounded-2xl overflow-hidden shadow-lg" data-testid="hybrid-calculation" style={{ background: '#fff', border: '1px solid rgba(107,168,46,0.35)' }}>
      {/* Green accent stripe */}
      <div className="h-[3px]" style={{ background: ACCENT_BAR_GRADIENT }} />

      {/* Header */}
      <div className="p-5 flex items-start justify-between flex-wrap gap-4" style={{ background: NAVY_GRADIENT }}>
        <div className="text-white flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-accent-soft">
            <Layers size={13} /> Hybrid Multi-Source Reconciliation
          </div>
          <div className="font-serif text-xl font-semibold mt-0.5">Reconciled Qualifying Income</div>
          <div className="text-white/70 text-xs mt-1">
            {hybrid.source_count} source{hybrid.source_count !== 1 ? 's' : ''} · {hybrid.employer_count} employer{hybrid.employer_count !== 1 ? 's' : ''} · confidence-weighted per Fannie B3-3.1-01/03
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-white/60 font-semibold">Total Qualifying Monthly</div>
          <div className="font-serif font-bold text-3xl mt-0.5 text-brand-accent-soft">{fmt$(hybrid.total_qualifying_monthly)}</div>
        </div>
      </div>

      {/* Warnings */}
      {(hybrid.reconciliation_warnings || []).length > 0 && (
        <div className="px-5 py-3 border-b border-slate-100" style={{ background: '#fef3c7' }}>
          {hybrid.reconciliation_warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2 text-[12px]" style={{ color: '#78350f' }}>
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {(hybrid.assumptions || []).length > 0 && (
        <div className="space-y-1 px-5 py-3 border-b border-slate-100" data-testid="hybrid-assumptions">
          {hybrid.assumptions.map((a) => <div key={a.field} className="text-xs text-slate-500">{a.note}</div>)}
        </div>
      )}

      {/* Employer rows */}
      <div className="divide-y divide-slate-100">
        {hybrid.employers.map((e, i) => {
          const isOpen = expanded.has(i);
          return (
            <div key={i} data-testid={`hybrid-employer-${i}`}>
              <button
                onClick={() => toggle(i)}
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50 text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: '#F3FAE8', color: '#3a5717' }}>
                    <Building2 size={15} />
                  </div>
                  <div>
                    <div className="font-serif text-[15px] font-bold text-brand-navy">{e.employer}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                      {e.sources.map((s, j) => (
                        <span key={j} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider" style={{ background: 'rgba(7,11,95,0.06)', color: BRAND.navy }}>
                          {s.type}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Qualifying/mo</div>
                  <div className="font-serif font-bold text-xl text-brand-accent">{fmt$(e.qualifying_monthly)}</div>
                </div>
              </button>

              {isOpen && (
                <div className="px-4 pb-4 space-y-3">
                  {/* Base panel */}
                  <div className="rounded-lg border p-3" style={{ background: 'rgba(107,168,46,0.06)', borderColor: 'rgba(107,168,46,0.3)' }}>
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#3a5717' }}>Base Pay (Reconciled)</div>
                      <div className="font-serif font-bold text-lg" style={{ color: '#3a5717' }}>{fmt$(e.chosen_base_monthly)}/mo</div>
                    </div>
                    <div className="text-[11px] text-slate-500 mb-2">Method: <b className="text-brand-navy">{e.base_method}</b></div>
                    <div className="space-y-1">
                      {Object.entries(e.base_sources).map(([k, v]) => {
                        const isChosen = k === e.chosen_base_key;
                        return (
                          <div key={k} className={`flex items-center justify-between text-[11px] rounded px-2 py-1 ${isChosen ? '' : 'opacity-70'}`} style={isChosen ? { background: 'rgba(107,168,46,0.15)' } : {}}>
                            <div className="flex items-center gap-1.5">
                              {isChosen ? <CheckCircle2 size={11} className="text-brand-accent" /> : <div className="w-[11px]" />}
                              <span className="font-semibold text-brand-navy">{sourceLabel(k)}</span>
                              <span className="text-slate-400">{v.source_file}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[9px] font-bold uppercase text-slate-400">conf {(v.confidence * 100).toFixed(0)}%</span>
                              <span className="font-semibold text-brand-navy">{fmt$(v.value)}/mo</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Variable panel */}
                  {Object.keys(e.variable_sources || {}).length > 0 && (
                    <div className="rounded-lg border p-3" style={{ background: e.chosen_variable_monthly > 0 ? 'rgba(245,158,11,0.06)' : '#f8fafc', borderColor: e.chosen_variable_monthly > 0 ? 'rgba(245,158,11,0.3)' : '#e2e8f0' }}>
                      <div className="flex items-baseline justify-between mb-2">
                        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: e.chosen_variable_monthly > 0 ? '#92400e' : '#64748b' }}>Variable Pay (Reconciled)</div>
                        <div className="font-serif font-bold text-lg" style={{ color: e.chosen_variable_monthly > 0 ? '#92400e' : '#64748b' }}>{fmt$(e.chosen_variable_monthly)}/mo</div>
                      </div>
                      <div className="text-[11px] text-slate-500 mb-2">Method: <b className="text-brand-navy">{e.variable_method}</b></div>
                      <div className="space-y-1">
                        {Object.entries(e.variable_sources).map(([k, v]) => (
                          <div key={k} className="flex items-center justify-between text-[11px]">
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold text-brand-navy">{sourceLabel(k)}</span>
                              <span className="text-slate-400">{v.source_file}</span>
                            </div>
                            <span className="font-semibold text-brand-navy">{v.value == null ? "can't calculate" : <>{fmt$(v.value)}/mo</>}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500">
        <Info size={11} className="inline mr-1" /> {hybrid.method_summary}
      </div>
    </div>
  );
}

function sourceLabel(key) {
  return {
    voe_current_base: 'VOE Base',
    paystub_current_base: 'Paystub Base',
    w2_prior_year_base: 'W-2 Prior Year',
    paystub_ytd_annualized: 'Paystub YTD',
    voe_ytd_averaged: 'VOE Avg (prior yr + YTD)',
  }[key] || key;
}
